<script lang="ts" setup>
import type {
  CMSCard,
  CMSHero,
  CMSListItem,
  CMSLocaleString,
  CMSPage
} from '#shared/types/cmsLayout'
import type {
  ExtendedBlogPostProps,
  ImageSlide,
  OptionsMap,
  StepItem
} from '~/types/elementsUi'
import type { ContentLayout } from '~/types/layouts'
import { getMetaTags } from '~/utils/meta'
import { textToKebabCase } from '~/utils/helpers'
import type { BreadcrumbItem } from '@nuxt/ui'
import type { ResponsiveImageProps } from '~/types/images.ts'

interface ExtendedCMSHero extends Omit<CMSHero, 'image'> {
  image: ResponsiveImageProps
}

interface CMSStaticLayoutProps {
  type: CMSPage['__type']
  data: CMSPage
  hero?: ExtendedCMSHero
  breadcrumbs: BreadcrumbItem[]
  preview?: boolean
  placeholder?: Record<string, number | undefined> // e.g. { amountOfAvailableCooks: 5 }
}

const props = defineProps<CMSStaticLayoutProps>()

const localeStore = useLocaleStore()

const locale = computed(() => localeStore.locale)

useSeoMeta(
  getMetaTags(
    {
      title: props.data.meta.title[locale.value],
      description: props.data.meta.description[locale.value],
      cachedAt: props.data.cachedAt
    },
    props.hero?.image.src
  )
)

const parseCarousel = (carousel: CMSListItem[]): ImageSlide[] => {
  return carousel.map((item, index) => ({
    name: item.title[locale.value],
    quote: item.content[locale.value],
    key: index
  }))
}

const parseList = (list: CMSListItem[]): ContentLayout[] => {
  return list.map((item) => ({
    title: item.title[locale.value],
    content: item.content[locale.value]
  }))
}

const parseSteps = (steps: CMSListItem[]): StepItem[] => {
  return steps.map((step) => ({
    title: step.title[locale.value],
    content: step.content[locale.value]
  }))
}

const parseLocations = (locations: CMSLocaleString[]): OptionsMap[] => {
  return locations.map((location) => {
    const currentLocation = location[localeStore.locale]
    return {
      name: currentLocation,
      id: textToKebabCase(currentLocation)
    }
  })
}
</script>

<template>
  <NuxtLayout
    name="default"
    :title="hero?.title[locale]"
    :image="hero?.image"
    :canonical="data.meta?.canonical"
    :ui="{
      image: 'lg:object-[center_-3rem]'
    }"
  >
    <template #subtitle>
      <h2
        v-if="hero?.subtitle?.[locale]"
        class="mb-5 text-h2 text-xl leading-5 font-normal text-gray-700 italic"
      >
        {{ hero.subtitle[locale] }}
      </h2>
    </template>

    <template #description>
      <VRenderMarkdown
        v-if="hero?.description[locale]"
        tag="p"
        class="text-lg"
        :content="hero.description[locale]"
        :placeholder
      />
    </template>

    <template #buttons>
      <VButtonRenderer
        v-for="(button, buttonIndex) in hero?.buttons"
        :key="`button-${buttonIndex}`"
        :attributes="{
          ...button,
          color: buttonIndex === 0 ? 'black' : 'beige',
          darkModeColor: buttonIndex === 0 ? 'beige' : 'success'
        }"
      />
    </template>

    <slot name="after-hero" />

    <VBreadcrumb :items="breadcrumbs" />

    <template
      v-for="(section, sectionIndex) in data.sections"
      :key="`section-${sectionIndex}`"
    >
      <ListSection
        v-if="section.blockType === 'number-list'"
        :title="section.title[locale]"
        :items="parseList(section.items)"
      >
        <template #after>
          <div class="section-button">
            <VButtonRenderer
              v-for="(button, buttonIndex) in section.buttons"
              :key="`button-${buttonIndex}`"
              :attributes="button"
            />
          </div>
        </template>
      </ListSection>

      <VSection v-else-if="section.blockType === 'bullet-points'">
        <template #content>
          <div class="columns flex-wrap text-left">
            <div
              v-for="(group, groupIndex) in section.groups"
              :key="groupIndex"
              class="column"
              :class="section.groups.length > 1 ? 'half' : 'full'"
            >
              <h2 class="title mb-4 text-[1.75rem]">
                {{ group.title[locale] }}
              </h2>

              <div
                v-for="(subGroup, subGroupIndex) in group.subGroups"
                :key="subGroupIndex"
                class="not-first:mt-6"
              >
                <p class="font-semibold">
                  {{ subGroup.content[locale] }}
                </p>

                <ul>
                  <li
                    v-for="(listItem, listItemIndex) in subGroup.items"
                    :key="`list-item-${listItemIndex}`"
                    class="mt-2"
                  >
                    <slot name="list-item" :item="listItem">
                      {{ listItem.title[locale] }}
                    </slot>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </template>
      </VSection>

      <TestimonialCarouselSection
        v-else-if="section.blockType === 'carousel'"
        :title="section.title[locale]"
        :slides="parseCarousel(section.items)"
      />

      <StepsSection
        v-else-if="section.blockType === 'steps'"
        :title="section.title[locale]"
        :subtitle="section.description[locale]"
        :items="parseSteps(section.items)"
      >
        <template #after>
          <div class="section-button">
            <VButtonRenderer
              v-for="(button, buttonIndex) in section.buttons"
              :key="`button-${buttonIndex}`"
              :attributes="button"
            />
          </div>
        </template>
      </StepsSection>

      <LocationsSection
        v-else-if="section.blockType === 'location'"
        :title="section.title[locale]"
        :description="section.description?.[locale] ?? ''"
        :locations="parseLocations(section.locations)"
      />

      <QuoteSection
        v-else-if="section.blockType === 'quote' || section.blockType === 'tip'"
        :type="section.blockType"
        :title="section.content[locale]"
        :person="section.title?.[locale]"
      />

      <VSection v-else-if="section.blockType === 'text'" :title="section.title?.[locale]">
        <slot name="text" :content="section.content">
          <VRenderMarkdown
            v-for="(paragraph, paragraphIndex) in section.content"
            :key="`paragraph-${paragraphIndex}`"
            tag="div"
            :content="paragraph[locale]"
            class="not-first:mt-4"
            :placeholder
          />
        </slot>

        <template #after>
          <div class="section-button">
            <VButtonRenderer
              v-for="(button, buttonIndex) in section.buttons"
              :key="`button-${buttonIndex}`"
              :attributes="button"
            />
          </div>
        </template>
      </VSection>

      <VSection v-else-if="section.blockType === 'news'" :title="section.title[locale]">
        <p>{{ section.description?.[locale] }}</p>

        <ul>
          <VRenderMarkdown
            v-for="(listItem, listItemIndex) in section.items"
            :key="`list-item-${listItemIndex}`"
            tag="li"
            :content="listItem[locale]"
            :class="
              type === 'focus-municipality-pages' && 'list-inside list-disc [&>p]:inline'
            "
            :placeholder
          />
        </ul>

        <template v-if="(section.contentAfter ?? []).length > 0">
          <VRenderMarkdown
            v-for="(paragraph, paragraphIndex) in section.contentAfter"
            :key="`paragraph-${paragraphIndex}`"
            tag="div"
            :content="paragraph[locale]"
            :placeholder
          />
        </template>
      </VSection>

      <template v-if="section.blockType === 'prebuilt'">
        <FAQSection v-if="section.type === 'faq'" :title="section.title[locale]" />

        <SocialMediaSection
          v-else-if="section.type === 'social-media'"
          :title="section.title[locale]"
        />

        <slot name="prebuilt-section" :section="section" />
      </template>
    </template>

    <slot name="prebuilt" />
  </NuxtLayout>
</template>

<style scoped></style>
